import {Kind} from 'graphql';

import type {ArgumentNode, DocumentNode, SelectionNode} from 'graphql';

export const INLINE_FRAGMENT_REFS_KEY = '__stripe_inlineFragmentRefs';
export const FRAGMENT_OWNER_DOCS_KEY = '__stripe_fragmentOwnerDocs';

export type HiddenDocumentMetadata = {
  inlineFragmentRefs?: Readonly<Record<string, DocumentNode>>;
  fragmentOwnerDocs?: Readonly<Record<string, DocumentNode>>;
};

type MetadataDocumentNode = DocumentNode & {
  [INLINE_FRAGMENT_REFS_KEY]?: Readonly<Record<string, DocumentNode>>;
  [FRAGMENT_OWNER_DOCS_KEY]?: Readonly<Record<string, DocumentNode>>;
};

export type CopyHiddenDocumentMetadataOptions = {
  preserveTargetValues?: boolean;
};

export function getInlineFragmentRefs(
  doc: DocumentNode,
): Readonly<Record<string, DocumentNode>> {
  return (doc as MetadataDocumentNode)[INLINE_FRAGMENT_REFS_KEY] ?? {};
}

export function getFragmentOwnerDocs(
  doc: DocumentNode,
): Readonly<Record<string, DocumentNode>> {
  return (doc as MetadataDocumentNode)[FRAGMENT_OWNER_DOCS_KEY] ?? {};
}

function isInlineFragmentRefName(name: string): boolean {
  return name.includes('__INLINE_REF_');
}

function getInlineFragmentRefNamesFromArgument(
  argument: ArgumentNode,
): ReadonlyArray<string> {
  if (argument.value.kind !== Kind.LIST) {
    return [];
  }

  return argument.value.values.flatMap((valueNode) => {
    if (valueNode.kind !== Kind.OBJECT) {
      return [];
    }

    const nameField = valueNode.fields.find(
      (field) => field.name.value === 'name',
    );

    if (!nameField || nameField.value.kind !== Kind.STRING) {
      return [];
    }

    return isInlineFragmentRefName(nameField.value.value)
      ? [nameField.value.value]
      : [];
  });
}

export function filterInlineFragmentRefsBySelections(
  inlineFragmentRefs: Readonly<Record<string, DocumentNode>>,
  selections: ReadonlyArray<SelectionNode>,
): Readonly<Record<string, DocumentNode>> {
  const referencedInlineFragmentRefNames = selections.reduce(
    (refs, selection) => {
      if (selection.kind !== Kind.FIELD) {
        return refs;
      }

      const paramsDirective = selection.directives?.find(
        (directive) => directive.name.value === 'params',
      );
      const fragmentsArgument = paramsDirective?.arguments?.find(
        (argument) => argument.name.value === 'fragments',
      );

      if (!fragmentsArgument) {
        return refs;
      }

      getInlineFragmentRefNamesFromArgument(fragmentsArgument).forEach(
        (fragmentRef) => {
          refs.add(fragmentRef);
        },
      );

      return refs;
    },
    new Set<string>(),
  );

  return Object.fromEntries(
    Object.entries(inlineFragmentRefs).filter(([fragmentRef]) =>
      referencedInlineFragmentRefNames.has(fragmentRef),
    ),
  );
}

export function attachHiddenDocumentMetadata<T extends DocumentNode>(
  target: T,
  metadata: HiddenDocumentMetadata,
  options: CopyHiddenDocumentMetadataOptions = {},
): T {
  const targetDoc = target as MetadataDocumentNode;
  const descriptors: PropertyDescriptorMap = {};
  const {inlineFragmentRefs, fragmentOwnerDocs} = metadata;

  // Keep these attachments off normal property enumeration so document cloning,
  // printing, and structural assertions continue to see only the GraphQL AST.
  if (inlineFragmentRefs && Object.keys(inlineFragmentRefs).length > 0) {
    descriptors[INLINE_FRAGMENT_REFS_KEY] = {
      value: options.preserveTargetValues
        ? targetDoc[INLINE_FRAGMENT_REFS_KEY] ?? inlineFragmentRefs
        : inlineFragmentRefs,
      enumerable: false,
      configurable: true,
    };
  }

  if (fragmentOwnerDocs && Object.keys(fragmentOwnerDocs).length > 0) {
    descriptors[FRAGMENT_OWNER_DOCS_KEY] = {
      value: options.preserveTargetValues
        ? targetDoc[FRAGMENT_OWNER_DOCS_KEY] ?? fragmentOwnerDocs
        : fragmentOwnerDocs,
      enumerable: false,
      configurable: true,
    };
  }

  if (Object.keys(descriptors).length === 0) {
    return target;
  }

  Object.defineProperties(target, descriptors);

  return target;
}

export function copyHiddenDocumentMetadata<T extends DocumentNode>(
  source: DocumentNode,
  target: T,
  options?: CopyHiddenDocumentMetadataOptions,
): T {
  return attachHiddenDocumentMetadata(
    target,
    {
      inlineFragmentRefs: getInlineFragmentRefs(source),
      fragmentOwnerDocs: getFragmentOwnerDocs(source),
    },
    options,
  );
}
